import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";

const BACKEND_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

export default function RazorpaySettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [toast, setToast] = useState(null);

  const [keyId, setKeyId] = useState("");
  const [keySecret, setKeySecret] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [keySecretDirty, setKeySecretDirty] = useState(false);
  const [webhookSecretDirty, setWebhookSecretDirty] = useState(false);
  const [showKeySecret, setShowKeySecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const loadConfig = async () => {
    try {
      const res = await api.get("/payment-gateway");
      const data = res.data;
      setConfig(data);
      if (data?.configured) {
        setKeyId(data.razorpay_key_id || "");
        setKeySecret(data.has_key_secret ? "••••••••••••••••" : "");
        setWebhookSecret(data.has_webhook_secret ? "••••••••••••••••" : "");
        setIsLiveMode(data.is_live_mode || false);
        setKeySecretDirty(false);
        setWebhookSecretDirty(false);
      }
    } catch {
      setConfig({ configured: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfig(); }, []);

  const handleSave = async () => {
    if (!keyId.trim()) return showToast("error", "Key ID is required");
    if (!config?.configured && !keySecret.trim()) return showToast("error", "Key Secret is required for initial setup");

    setSaving(true);
    try {
      const payload = { razorpay_key_id: keyId.trim(), is_live_mode: isLiveMode };
      if (keySecretDirty && keySecret.trim()) payload.razorpay_key_secret = keySecret;
      if (webhookSecretDirty && webhookSecret.trim()) payload.razorpay_webhook_secret = webhookSecret;

      await api.post("/payment-gateway", payload);
      await loadConfig();
      showToast("success", "Razorpay configured successfully");
    } catch (err) {
      showToast("error", err?.response?.data?.message || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Remove Razorpay configuration? This cannot be undone.")) return;
    setDisconnecting(true);
    try {
      await api.delete("/payment-gateway");
      setConfig({ configured: false });
      setKeyId(""); setKeySecret(""); setWebhookSecret("");
      setIsLiveMode(false); setKeySecretDirty(false); setWebhookSecretDirty(false);
      showToast("success", "Razorpay disconnected");
    } catch {
      showToast("error", "Failed to disconnect");
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Icon name="Loader2" size={18} color="var(--color-muted-foreground)" className="animate-spin" />
      </div>
    );
  }

  const isConfigured = config?.configured;
  const modeLabel = isConfigured
    ? isLiveMode ? "Live Mode" : "Test Mode"
    : null;

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium font-[var(--font-caption)] ${
          toast.type === "success"
            ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
            : "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]"
        }`}>
          <Icon name={toast.type === "success" ? "CheckCircle" : "AlertCircle"} size={13} />
          {toast.message}
        </div>
      )}

      {/* Status banner */}
      <div className={`flex items-center justify-between px-3 py-2.5 rounded-md border ${
        isConfigured
          ? isLiveMode
            ? "border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30"
            : "border-[var(--color-success)]/30 bg-[var(--color-success)]/5"
          : "border-[var(--color-border)] bg-[var(--color-muted)]/30"
      }`}>
        <div className="flex items-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full ${
            isConfigured ? (isLiveMode ? "bg-blue-500" : "bg-[var(--color-success)]") : "bg-[var(--color-muted-foreground)]"
          }`} />
          <span className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-foreground)]">
            {isConfigured ? `Connected · ${modeLabel}` : "Not connected"}
          </span>
        </div>
        {isConfigured && (
          <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            {config.razorpay_key_id}
          </span>
        )}
      </div>

      {/* Form */}
      <div className="space-y-3">
        {/* Key ID */}
        <div>
          <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1">
            Key ID <span className="text-[var(--color-destructive)]">*</span>
          </label>
          <input
            type="text"
            value={keyId}
            onChange={e => setKeyId(e.target.value)}
            placeholder="rzp_test_xxxxxxxxxxxx"
            className="w-full px-3 py-2 text-xs font-[var(--font-data)] bg-[var(--color-card)] border border-[var(--color-border)] rounded-md text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
          />
        </div>

        {/* Key Secret */}
        <div>
          <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1">
            Key Secret {!isConfigured && <span className="text-[var(--color-destructive)]">*</span>}
            {isConfigured && <span className="ml-1 text-[var(--color-muted-foreground)] font-normal">(leave blank to keep existing)</span>}
          </label>
          <div className="relative">
            <input
              type={showKeySecret ? "text" : "password"}
              value={keySecret}
              onFocus={() => { if (!keySecretDirty) setKeySecret(""); }}
              onChange={e => { setKeySecret(e.target.value); setKeySecretDirty(true); }}
              placeholder={isConfigured ? "Enter new secret to update" : "rzp_secret_xxxxxxxxxxxx"}
              className="w-full px-3 py-2 pr-9 text-xs font-[var(--font-data)] bg-[var(--color-card)] border border-[var(--color-border)] rounded-md text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
            />
            <button
              type="button"
              onClick={() => setShowKeySecret(v => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
            >
              <Icon name={showKeySecret ? "EyeOff" : "Eye"} size={13} />
            </button>
          </div>
        </div>

        {/* Webhook Secret */}
        <div>
          <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1">
            Webhook Secret
            <span className="ml-1 text-[var(--color-muted-foreground)] font-normal">(optional — required for webhook verification)</span>
          </label>
          <div className="relative">
            <input
              type={showWebhookSecret ? "text" : "password"}
              value={webhookSecret}
              onFocus={() => { if (!webhookSecretDirty) setWebhookSecret(""); }}
              onChange={e => { setWebhookSecret(e.target.value); setWebhookSecretDirty(true); }}
              placeholder={isConfigured && config?.has_webhook_secret ? "Enter new secret to update" : "Webhook secret from Razorpay Dashboard"}
              className="w-full px-3 py-2 pr-9 text-xs font-[var(--font-data)] bg-[var(--color-card)] border border-[var(--color-border)] rounded-md text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
            />
            <button
              type="button"
              onClick={() => setShowWebhookSecret(v => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
            >
              <Icon name={showWebhookSecret ? "EyeOff" : "Eye"} size={13} />
            </button>
          </div>
        </div>

        {/* Live / Test toggle */}
        <div className="flex items-center justify-between p-3 rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/20">
          <div>
            <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-foreground)]">Live Mode</p>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
              {isLiveMode ? "Using live keys — real transactions" : "Using test keys — no real money"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsLiveMode(v => !v)}
            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
              isLiveMode ? "bg-blue-500" : "bg-[var(--color-muted)]"
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isLiveMode ? "translate-x-4" : "translate-x-0"}`} />
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-1">
        {isConfigured ? (
          <button
            type="button"
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium font-[var(--font-caption)] text-[var(--color-destructive)] border border-[var(--color-destructive)]/30 rounded-md hover:bg-[var(--color-destructive)]/5 transition-colors disabled:opacity-60"
          >
            {disconnecting ? <Icon name="Loader2" size={12} className="animate-spin" /> : <Icon name="Unplug" size={12} />}
            Disconnect
          </button>
        ) : <div />}

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium font-[var(--font-caption)] bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60"
        >
          {saving ? <Icon name="Loader2" size={12} color="white" className="animate-spin" /> : <Icon name="Save" size={12} color="white" />}
          {saving ? "Saving..." : isConfigured ? "Save Changes" : "Connect Razorpay"}
        </button>
      </div>

      {/* Info box */}
      <div className="p-3 rounded-md bg-[var(--color-muted)]/30 border border-[var(--color-border)] space-y-1.5">
        <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-foreground)]">Setup guide</p>
        <ul className="space-y-1">
          <li className="flex items-start gap-1.5 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] flex items-center justify-center text-[10px] font-bold">1</span>
            Get your API keys from the Razorpay Dashboard → Settings → API Keys
          </li>
          <li className="flex items-start gap-1.5 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] flex items-center justify-center text-[10px] font-bold">2</span>
            For webhooks, go to Settings → Webhooks → Add new webhook, set URL to:
            <code className="ml-1 px-1 bg-[var(--color-muted)] rounded text-[10px] font-[var(--font-data)] text-[var(--color-foreground)] break-all">
              {BACKEND_URL}/razorpay/webhook
            </code>
          </li>
          <li className="flex items-start gap-1.5 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] flex items-center justify-center text-[10px] font-bold">3</span>
            Copy the webhook secret from Razorpay and paste it above
          </li>
        </ul>
      </div>
    </div>
  );
}
